import React, { useRef, useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import LandingPage from "./LandingPage";
import BlogPage from "./BlogPage";
import BlogDetailPage from "./pages/BlogDetailPage";
import { Dashboard } from "./Dashboard";
import { Notifications } from "./Notifications";
import { Portfolio } from "./Portofolio";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Login } from "./Login";
import { Signup } from "./SignUp";
import { Account } from "./Account";
import { AdminPanel } from "./AdminPanel";
import AddUserForm from "./adminpanel/AddUserForm";
import UserEditForm from "./adminpanel/UserEditForm";
import { SubscriptionDetails } from "./SubscriptionDetails";
import TermsOfUse from "./TermsOfUse";
import PrivacyPolicy from "./PrivacyPolicy";
import { XenoRush } from "./Xenorush";
import AdminLogin from "./adminpanel/AdminLogin";
import WithdrawalsPage from "./adminpanel/WithdrawalPage";
import AdminMain from "./adminpanel/AdminMain";
import { AdminAuthProvider } from "./AdminAuthContext";
import AdminDashboard from "./adminpanel/AdminDashboard";
import Settings from "./adminpanel/Settings";
import ArticleUpload from "./adminpanel/ArticleUpload";
import { BotList } from "./BotList";
import { FAQ } from "./FAQ";
import Backtest from "./Backtest";
import ProtectedRoute from "./utils/ProtectedRoute";
import { AuthProvider, useAuth } from "./context/authContext";
import ArticleManage from "./adminpanel/ArticleManage";
import ArticleEdit from "./adminpanel/ArticleEdit";
import NavbarMobile from "./NavbarMobile";
import { SidebarProvider } from "./context/sidebarContext";
import GetSecretCode from "./event/treasurehunt/GetSecretCode";
import { BotProvider } from "./context/botContext";
import { ToastProvider } from "./context/toastContext";
import { LearnPage } from "./LearnPage";
import { LearnSubCourse } from "./LearnSubCourse";
import { LearnContent } from "./LearnContent";
import { LearnProgress } from "./LearnProgress";
import { LearnExam } from "./LearnExam";
import { Certificate } from "./Certificate";
import { PaperTrading } from "./PaperTrading";
import { AdsProvider } from "./context/adsContext";
import { TestingMode } from "./TestingMode";
import ForwardTestingBot from "./ForwardTestingBot";
import { BotDemoProvider } from "./context/botDemoContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";
import XenoarcPage from "./XenoarcPage";
import DCAPage from "./DCAPage";
import GRIDPage from "./GRIDPage";
import LegalDisclaimerPage from "./LegalDisclaimerPage";
import SetPassword from "./SetPassword";
import PricingPage from "./PricingPage";
import EventsPage from "./EventPage";
import EventDetailPage from "./EventDetailPage";

// Create a client
const queryClient = new QueryClient();

const RedirectBlogToDigest = () => {
  const { slug } = useParams();
  return <Navigate to={`/digest/${slug}`} replace />;
};

const AppContent = () => {
  const location = useLocation();
  const path = location.pathname;
  const { user, isLoading } = useAuth();
  // const shouldHideLayout = path.startsWith("/admin/") || path !== "/";
  const showNavbarMobile = !!user;
  const shouldHideLayout =
    path.startsWith("/admin") ||
    (path.startsWith("/digest") ||
    path.startsWith("/pricing") ||
    path.includes("/event") ||
    path.includes("/learn-more") ||
    path.includes("/legal-disclaimer")
      ? false
      : path !== "/");
  // const shouldHideLayout = path.startsWith("/admin") || path === "/login" || path === "/signup";

  const botRef = useRef(null);
  const joinRef = useRef(null);
  const learnRef = useRef(null);

  const smoothScrollTo = (to, duration = 500) => {
    const start = window.scrollY;
    const change = to - start;
    const startTime = performance.now();

    function animateScroll(currentTime) {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easeInOutQuad =
        progress < 0.5
          ? 2 * progress * progress
          : -1 + (4 - 2 * progress) * progress;

      window.scrollTo(0, start + change * easeInOutQuad);

      if (elapsed < duration) {
        requestAnimationFrame(animateScroll);
      }
    }

    requestAnimationFrame(animateScroll);
  };

  const scrollToSection = (ref) => {
    const top = ref.current?.offsetTop;
    if (top !== undefined) {
      smoothScrollTo(top);
    }
  };

  useEffect(() => {
    const target = location.state?.scrollTarget;
    if (target) {
      const refMap = { bot: botRef, join: joinRef, learn: learnRef };
      const ref = refMap[target];
      if (ref?.current) {
        setTimeout(() => scrollToSection(ref), 400);
      }
    }
  }, [location.state]);

  // hide notificaiton connected exchange
  useEffect(() => {
    if (localStorage.getItem("exhchange_connected")) {
      localStorage.removeItem("exhchange_connected");
    }
  }, [location]);

  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => {
    setSidebarOpen(!isSidebarOpen);
  };

  return (
    <>
      {!shouldHideLayout && (
        // {!shouldHideLayout && (
        <Navbar
          scrollToSection={scrollToSection}
          botRef={botRef}
          joinRef={joinRef}
          learnRef={learnRef}
        />
      )}

      {showNavbarMobile && shouldHideLayout && (
        <NavbarMobile onDrawerClick={toggleSidebar} />
      )}
      <Routes>
        <Route
          path="/"
          element={
            <LandingPage
              scrollToSection={scrollToSection}
              botRef={botRef}
              joinRef={joinRef}
              learnRef={learnRef}
            />
          }
        />
        <Route path="/events" element={<EventsPage />} />
        <Route path="/events/:slug" element={<EventDetailPage />} />
        <Route path="/legal-disclaimer" element={<LegalDisclaimerPage />} />
        <Route path="/learn-more/xenoarc" element={<XenoarcPage />} />
        <Route path="/learn-more/dca" element={<DCAPage />} />
        <Route path="/learn-more/grid" element={<GRIDPage />} />
        <Route path="/blog" element={<Navigate to="/digest" replace />} />
        <Route path="/blog/:slug" element={<RedirectBlogToDigest />} />
        <Route path="/digest" element={<BlogPage />} />
        <Route path="/digest/:slug" element={<BlogDetailPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bot-list"
          element={
            <ProtectedRoute>
              <BotList />
            </ProtectedRoute>
          }
        />
        <Route path="/terms-of-use" element={<TermsOfUse />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route
          path="/notification"
          element={
            <ProtectedRoute>
              <Notifications />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolio"
          element={
            <ProtectedRoute>
              <Portfolio />
            </ProtectedRoute>
          }
        />
        <Route
          path="/testing-mode"
          element={
            <ProtectedRoute>
              <TestingMode />
            </ProtectedRoute>
          }
        />
        <Route
          path="/testing-mode/trading"
          element={
            <ProtectedRoute>
              <PaperTrading />
            </ProtectedRoute>
          }
        />
        <Route
          path="/testing-mode/back-test"
          element={
            <ProtectedRoute>
              <Backtest />
            </ProtectedRoute>
          }
        />{" "}
        <Route
          path="/testing-mode/forward-testing-bot"
          element={
            <ProtectedRoute>
              <ForwardTestingBot />
            </ProtectedRoute>
          }
        />
        <Route
          path="/learn"
          element={
            <ProtectedRoute>
              <LearnPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/learn/subcourse/:course_id"
          element={
            <ProtectedRoute>
              <LearnSubCourse />
            </ProtectedRoute>
          }
        />
        <Route
          path="/learn/subcourse/course/:lesson_id"
          element={
            <ProtectedRoute>
              <LearnContent />
            </ProtectedRoute>
          }
        />
        <Route
          path="/learn/progress"
          element={
            <ProtectedRoute>
              <LearnProgress />
            </ProtectedRoute>
          }
        />
        <Route
          path="/learn/exam/:exam_id"
          element={
            <ProtectedRoute>
              <LearnExam />
            </ProtectedRoute>
          }
        />
        <Route
          path="/certificate"
          element={
            <ProtectedRoute>
              <Certificate />
            </ProtectedRoute>
          }
        />
        <Route
          path="/login"
          element={
            <GoogleOAuthProvider clientId="726294937749-ftar8ftcgqaq0k1smojghoip0o04mi12.apps.googleusercontent.com">
              <Login />
            </GoogleOAuthProvider>
          }
        />
        <Route
          path="/signup"
          element={
            <GoogleOAuthProvider clientId="726294937749-ftar8ftcgqaq0k1smojghoip0o04mi12.apps.googleusercontent.com">
              <Signup />
            </GoogleOAuthProvider>
          }
        />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/faq" element={<FAQ />} />
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <Account />
            </ProtectedRoute>
          }
        />
        <Route path="/xenorush" element={<XenoRush />} />
        <Route path="/admin-panel" element={<AdminPanel />} />
        <Route path="/user-add" element={<AddUserForm />} />
        <Route path="/get-code" element={<GetSecretCode />} />
        <Route path="/user-edit/:user_id" element={<UserEditForm />} />
        <Route
          path="/subscription-details/:customerId"
          element={<SubscriptionDetails />}
        />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminMain />}>
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="withdrawals" element={<WithdrawalsPage />} />
          <Route path="settings" element={<Settings />} />
          <Route path="article-upload" element={<ArticleUpload />} />
          <Route path="article-manage" element={<ArticleManage />} />
          <Route path="article-edit/:slug" element={<ArticleEdit />} />
        </Route>
        <Route path="/admin-dashboard" element={<AdminDashboard />} />
      </Routes>
      {!shouldHideLayout && <Footer />}
      {/* <Footer /> */}
      {/* // {!shouldHideLayout && <Footer />} */}
    </>
  );
};

function App() {
  return (
    <Router>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <AuthProvider>
            <SidebarProvider>
              <AdminAuthProvider>
                <BotProvider>
                  <BotDemoProvider>
                    <AdsProvider>
                      <AppContent />
                    </AdsProvider>
                  </BotDemoProvider>
                </BotProvider>
              </AdminAuthProvider>
            </SidebarProvider>
          </AuthProvider>
        </ToastProvider>
      </QueryClientProvider>
    </Router>
  );
}

export default App;
